import { useEffect, useRef, useState, useCallback, useSyncExternalStore } from "react";

// ---------------- global speech controller (read-aloud with controls) ----------------
const RATE_KEY = "lifeos_speech_rate";
const controller = {
  listeners: new Set(),
  lastText: "",
  rate: Number(localStorage.getItem(RATE_KEY)) || 1,
  speaking: false,
  paused: false,
  _emit() { this.listeners.forEach((l) => l()); },
  subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); },
  getSnapshot() { return `${this.speaking}|${this.paused}|${this.rate}`; },
  speak(text) {
    if (!("speechSynthesis" in window) || !text) return;
    const clean = typeof text === "string" ? text : JSON.stringify(text);
    window.speechSynthesis.cancel();
    this.lastText = clean;
    const u = new SpeechSynthesisUtterance(clean);
    u.rate = this.rate;
    u.onend = () => { this.speaking = false; this.paused = false; this._emit(); };
    u.onerror = () => { this.speaking = false; this.paused = false; this._emit(); };
    this.speaking = true; this.paused = false; this._emit();
    window.speechSynthesis.speak(u);
  },
  pause() { if (this.speaking && !this.paused) { window.speechSynthesis.pause(); this.paused = true; this._emit(); } },
  resume() { if (this.paused) { window.speechSynthesis.resume(); this.paused = false; this._emit(); } },
  replay() { if (this.lastText) this.speak(this.lastText); },
  stop() { window.speechSynthesis?.cancel(); this.speaking = false; this.paused = false; this._emit(); },
  setRate(r) {
    this.rate = Math.min(1.6, Math.max(0.5, +r.toFixed(2)));
    localStorage.setItem(RATE_KEY, String(this.rate));
    this._emit();
    if (this.speaking) this.replay();
  },
};

export function speak(text, opts = {}) {
  if (opts.rate) controller.rate = opts.rate;
  controller.speak(text);
}
export function stopSpeak() { controller.stop(); }

export function useSpeech() {
  useSyncExternalStore(controller.subscribe.bind(controller), controller.getSnapshot.bind(controller));
  return {
    speaking: controller.speaking,
    paused: controller.paused,
    rate: controller.rate,
    speak: (t) => controller.speak(t),
    pause: () => controller.pause(),
    resume: () => controller.resume(),
    replay: () => controller.replay(),
    stop: () => controller.stop(),
    setRate: (r) => controller.setRate(r),
    supported: "speechSynthesis" in window,
  };
}

// ---------------- speech input (voice-to-text) ----------------
export function useSpeechInput(onResult) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const recRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setSupported(true);
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = "en-US";
    rec.onresult = (e) => { const t = e.results[0][0].transcript; onResult && onResult(t); };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    return () => { try { rec.abort(); } catch (_) {} };
  }, [onResult]);

  const start = useCallback(() => { if (recRef.current) { try { recRef.current.start(); setListening(true); } catch (_) {} } }, []);
  const stop = useCallback(() => { if (recRef.current) recRef.current.stop(); setListening(false); }, []);
  return { listening, supported, start, stop };
}
